import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { User, Mail, Phone, MapPin, Calendar, Edit, Save, X, Users, Building2, Briefcase, CreditCard, Shield, LogOut, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { lengthOfService } from '../lib/serviceLength';

export default function MyProfile() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({});

  // Iter88k — Resignation request state.
  const [myResignations, setMyResignations] = useState([]);
  const [resignDialogOpen, setResignDialogOpen] = useState(false);
  const [resignForm, setResignForm] = useState({
    last_working_day: '',
    reason: '',
  });
  const [resignSubmitting, setResignSubmitting] = useState(false);
  // Iter88p — Notice period is now read-only from company HR policy.
  const [policyNoticeDays, setPolicyNoticeDays] = useState(30);

  const user = JSON.parse(localStorage.getItem('hrms_user') || '{}');

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/employees/${user.employee_id}`);
      setProfile(response.data);
      setFormData(response.data);
    } catch (error) {
      toast.error('Failed to fetch profile');
    } finally {
      setLoading(false);
    }
  };

  const fetchMyResignations = async () => {
    try {
      const r = await axios.get(`${API}/resignations`);
      setMyResignations(Array.isArray(r.data) ? r.data : []);
    } catch (_) {
      setMyResignations([]);
    }
  };

  // Iter88p — Read company HR policy so the resignation dialog can show
  // the notice period as a read-only value derived from policy.
  const fetchHrPolicy = async () => {
    try {
      const r = await axios.get(`${API}/hr-policy`);
      setPolicyNoticeDays(Number(r.data?.default_notice_period_days) || 30);
    } catch (_) {
      setPolicyNoticeDays(30);
    }
  };

  useEffect(() => {
    fetchProfile();
    fetchMyResignations();
    fetchHrPolicy();
  }, []);

  const submitResignation = async () => {
    // Iter126 — Tell the user EXACTLY which required field is missing.
    // Previously the message said "Please fill last working day and reason"
    // even when only one of them was empty, making the tester think the
    // already-selected date was also being rejected. Reported by Ankur
    // 18-Jun-2026.
    const missing = [];
    if (!resignForm.last_working_day) missing.push('Last Working Day');
    if (!resignForm.reason.trim()) missing.push('Reason for Resignation');
    if (missing.length) {
      toast.error(
        missing.length === 1
          ? `Please fill the ${missing[0]} field.`
          : `Please fill the ${missing.join(' and ')} fields.`
      );
      return;
    }
    setResignSubmitting(true);
    try {
      await axios.post(`${API}/resignations`, resignForm);
      toast.success('Resignation request submitted. HR will review it shortly.');
      setResignDialogOpen(false);
      setResignForm({ last_working_day: '', reason: '' });
      fetchMyResignations();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to submit resignation request.');
    } finally {
      setResignSubmitting(false);
    }
  };

  const withdrawResignation = async (resignation_id) => {
    if (!window.confirm('Withdraw this resignation request? This cannot be undone.')) return;
    try {
      await axios.post(`${API}/resignations/${resignation_id}/withdraw`);
      toast.success('Resignation withdrawn.');
      fetchMyResignations();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to withdraw.');
    }
  };

  const handleSave = async () => {
    try {
      await axios.put(`${API}/me/profile`, formData);
      toast.success('Profile updated successfully');
      setProfile(formData);
      setEditing(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    }
  };

  const handleCancel = () => {
    setFormData(profile);
    setEditing(false);
  };

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 dark:border-slate-100"></div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500 dark:text-slate-400">Profile not found</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">My Profile</h2>
          <p className="text-slate-600 dark:text-slate-400 mt-1">Manage your personal information</p>
        </div>
        {!editing ? (
          <div className="flex gap-2 flex-wrap">
            {/* Iter88k — Resignation entry point for employees. We let
                everyone (including demo accounts) submit and withdraw — it's
                only a request. The backend's approval endpoint refuses to
                actually terminate the 5 demo accounts to keep the test
                environment intact, so demos can still walk through the
                whole flow without burning a tester account. */}
            {profile?.status === 'active' && (() => {
              const hasPending = myResignations.some(r => r.status === 'pending');
              const openDialog = () => {
                // Iter88v — Pre-fill LWD with earliest valid date (today + notice).
                const d = new Date(Date.now() + 8 * 3600 * 1000);
                d.setUTCDate(d.getUTCDate() + (Number(policyNoticeDays) || 0));
                setResignForm((f) => ({
                  ...f,
                  last_working_day: f.last_working_day || d.toISOString().slice(0, 10),
                }));
                setResignDialogOpen(true);
              };
              return (
                <Button
                  variant="outline"
                  onClick={openDialog}
                  disabled={hasPending}
                  data-testid="open-resign-dialog-btn"
                  title={
                    hasPending
                      ? 'You already have a pending resignation. Withdraw it before submitting a new one.'
                      : 'Submit a resignation request to HR'
                  }
                  className="border-red-300 text-red-600 hover:bg-red-50 dark:border-red-700 dark:text-red-400 dark:hover:bg-red-900/20 disabled:opacity-50"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Request Resignation
                </Button>
              );
            })()}
            <Button
              onClick={() => setEditing(true)}
              className="bg-slate-900 hover:bg-slate-800 dark:bg-emerald-600 dark:hover:bg-emerald-700"
              data-testid="edit-profile-btn"
            >
              <Edit className="h-4 w-4 mr-2" />
              Edit Profile
            </Button>
          </div>
        ) : (
          <div className="flex gap-2 flex-wrap">
            <Button
              onClick={handleCancel}
              variant="outline"
              data-testid="cancel-profile-btn"
            >
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="save-profile-btn"
            >
              <Save className="h-4 w-4 mr-2" />
              Save Changes
            </Button>
          </div>
        )}
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 overflow-hidden">
        {/* Header Section */}
        <div className="bg-gradient-to-r from-emerald-500 to-teal-500 p-8">
          <div className="flex items-center space-x-4">
            <div className="h-20 w-20 rounded-full bg-white flex items-center justify-center">
              <User className="h-10 w-10 text-emerald-600" />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-white">{profile.full_name}</h3>
              <p className="text-emerald-100">{profile.position || 'Employee'}</p>
              <p className="text-sm text-emerald-50 mt-1">{profile.employee_id}</p>
            </div>
          </div>
        </div>

        {/* Profile Information */}
        <div className="p-8 space-y-8">
          {/* Personal Information */}
          <div>
            <div className="flex items-center space-x-2 mb-4">
              <User className="h-5 w-5 text-emerald-600" />
              <h4 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Personal Information</h4>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label>Full Name</Label>
                {editing ? (
                  <Input
                    value={formData.full_name}
                    onChange={(e) => handleInputChange('full_name', e.target.value)}
                  />
                ) : (
                  <p className="text-slate-900 dark:text-slate-100 font-medium">{profile.full_name}</p>
                )}
              </div>
              
              <div>
                <Label>Email</Label>
                {editing ? (
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                  />
                ) : (
                  <p className="text-slate-900 dark:text-slate-100 font-medium">{profile.email}</p>
                )}
              </div>
              
              <div>
                <Label>Phone Number</Label>
                {editing ? (
                  <Input
                    value={formData.phone || ''}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    placeholder="+60 12-345 6789"
                  />
                ) : (
                  <p className="text-slate-900 dark:text-slate-100 font-medium">{profile.phone || 'Not provided'}</p>
                )}
              </div>
              
              <div>
                <Label>IC Number / Passport</Label>
                {editing ? (
                  <Input
                    value={formData.ic_number || ''}
                    onChange={(e) => handleInputChange('ic_number', e.target.value)}
                    placeholder="123456-78-9012"
                  />
                ) : (
                  <p className="text-slate-900 dark:text-slate-100 font-medium">{profile.ic_number || 'Not provided'}</p>
                )}
              </div>

              <div>
                <Label>Nationality</Label>
                {editing ? (
                  <Select
                    value={formData.nationality || 'malaysian'}
                    onValueChange={(value) => handleInputChange('nationality', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="malaysian">Malaysian</SelectItem>
                      <SelectItem value="non_malaysian_permanent">Non-Malaysian (Permanent Resident)</SelectItem>
                      <SelectItem value="non_malaysian_temporary">Non-Malaysian (Temporary)</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="text-slate-900 dark:text-slate-100 font-medium capitalize">
                    {profile.nationality?.replace(/_/g, ' ') || 'Malaysian'}
                  </p>
                )}
              </div>

              <div>
                <Label>Marital Status</Label>
                {editing ? (
                  <Select
                    value={formData.marital_status || 'single'}
                    onValueChange={(value) => handleInputChange('marital_status', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="single">Single</SelectItem>
                      <SelectItem value="married">Married</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="text-slate-900 dark:text-slate-100 font-medium capitalize">
                    {profile.marital_status || 'Single'}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Family & Dependants */}
          <div>
            <div className="flex items-center space-x-2 mb-4">
              <Users className="h-5 w-5 text-emerald-600" />
              <h4 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Family & Dependants</h4>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label>Spouse Working?</Label>
                {editing ? (
                  <Select
                    value={formData.spouse_working ? 'yes' : 'no'}
                    onValueChange={(value) => handleInputChange('spouse_working', value === 'yes')}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">Yes</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="text-slate-900 dark:text-slate-100 font-medium">
                    {profile.spouse_working ? 'Yes' : 'No'}
                  </p>
                )}
              </div>
              
              <div>
                <Label>Number of Children</Label>
                {editing ? (
                  <Input
                    type="number"
                    min="0"
                    value={formData.number_of_children || 0}
                    onChange={(e) => handleInputChange('number_of_children', parseInt(e.target.value) || 0)}
                  />
                ) : (
                  <p className="text-slate-900 dark:text-slate-100 font-medium">{profile.number_of_children || 0}</p>
                )}
              </div>

              <div>
                <Label>Disabled Self?</Label>
                {editing ? (
                  <Select
                    value={formData.disabled_self ? 'yes' : 'no'}
                    onValueChange={(value) => handleInputChange('disabled_self', value === 'yes')}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">Yes</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="text-slate-900 dark:text-slate-100 font-medium">
                    {profile.disabled_self ? 'Yes' : 'No'}
                  </p>
                )}
              </div>

              <div>
                <Label>Disabled Spouse?</Label>
                {editing ? (
                  <Select
                    value={formData.disabled_spouse ? 'yes' : 'no'}
                    onValueChange={(value) => handleInputChange('disabled_spouse', value === 'yes')}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">Yes</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="text-slate-900 dark:text-slate-100 font-medium">
                    {profile.disabled_spouse ? 'Yes' : 'No'}
                  </p>
                )}
              </div>

              <div>
                <Label>Number of Disabled Children</Label>
                {editing ? (
                  <Input
                    type="number"
                    min="0"
                    value={formData.disabled_children || 0}
                    onChange={(e) => handleInputChange('disabled_children', parseInt(e.target.value) || 0)}
                  />
                ) : (
                  <p className="text-slate-900 dark:text-slate-100 font-medium">{profile.disabled_children || 0}</p>
                )}
              </div>
            </div>
          </div>

          {/* Employment Details */}
          <div>
            <div className="flex items-center space-x-2 mb-4">
              <Briefcase className="h-5 w-5 text-emerald-600" />
              <h4 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Employment Details</h4>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label>Department</Label>
                <p className="text-slate-900 dark:text-slate-100 font-medium">{profile.department || 'N/A'}</p>
              </div>
              
              <div>
                <Label>Position</Label>
                <p className="text-slate-900 dark:text-slate-100 font-medium">{profile.position || 'N/A'}</p>
              </div>
              
              <div>
                <Label>Join Date</Label>
                <p className="text-slate-900 dark:text-slate-100 font-medium">
                  {new Date(profile.join_date).toLocaleDateString('en-MY', { 
                    day: '2-digit', 
                    month: 'long', 
                    year: 'numeric' 
                  })}
                </p>
              </div>

              <div>
                <Label>Length of Service</Label>
                <p className="text-slate-900 dark:text-slate-100 font-medium" data-testid="profile-service-length">
                  {lengthOfService(profile.join_date)?.label || 'N/A'}
                </p>
              </div>
              
              <div>
                <Label>Employment Type</Label>
                <p className="text-slate-900 dark:text-slate-100 font-medium capitalize">
                  {profile.employment_type?.replace('_', ' ')}
                </p>
              </div>
              
              {profile.manager_name && (
                <div>
                  <Label>Reporting Manager</Label>
                  <p className="text-slate-900 dark:text-slate-100 font-medium">{profile.manager_name}</p>
                </div>
              )}
            </div>
          </div>

          {/* Banking Information */}
          <div>
            <div className="flex items-center space-x-2 mb-4">
              <CreditCard className="h-5 w-5 text-emerald-600" />
              <h4 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Banking Information</h4>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label>Bank Name</Label>
                {editing ? (
                  <Input
                    value={formData.bank_name || ''}
                    onChange={(e) => handleInputChange('bank_name', e.target.value)}
                    placeholder="e.g. Maybank, CIMB, Public Bank"
                  />
                ) : (
                  <p className="text-slate-900 dark:text-slate-100 font-medium">{profile.bank_name || 'Not provided'}</p>
                )}
              </div>
              
              <div>
                <Label>Bank Account Number</Label>
                {editing ? (
                  <Input
                    value={formData.bank_account || ''}
                    onChange={(e) => handleInputChange('bank_account', e.target.value)}
                    placeholder="Account number"
                  />
                ) : (
                  <p className="text-slate-900 dark:text-slate-100 font-medium">{profile.bank_account || 'Not provided'}</p>
                )}
              </div>
            </div>
          </div>

          {/* Statutory Information */}
          <div>
            <div className="flex items-center space-x-2 mb-4">
              <Shield className="h-5 w-5 text-emerald-600" />
              <h4 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Statutory Information</h4>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label>EPF Number</Label>
                {editing ? (
                  <Input
                    value={formData.epf_number || ''}
                    onChange={(e) => handleInputChange('epf_number', e.target.value)}
                    placeholder="EPF number"
                  />
                ) : (
                  <p className="text-slate-900 dark:text-slate-100 font-medium">{profile.epf_number || 'Not provided'}</p>
                )}
              </div>
              
              <div>
                <Label>SOCSO Number</Label>
                {editing ? (
                  <Input
                    value={formData.socso_number || ''}
                    onChange={(e) => handleInputChange('socso_number', e.target.value)}
                    placeholder="SOCSO number"
                  />
                ) : (
                  <p className="text-slate-900 dark:text-slate-100 font-medium">{profile.socso_number || 'Not provided'}</p>
                )}
              </div>

              <div>
                <Label>EIS Number</Label>
                {editing ? (
                  <Input
                    value={formData.eis_number || ''}
                    onChange={(e) => handleInputChange('eis_number', e.target.value)}
                    placeholder="EIS number"
                  />
                ) : (
                  <p className="text-slate-900 dark:text-slate-100 font-medium">{profile.eis_number || 'Not provided'}</p>
                )}
              </div>

              <div>
                <Label>Income Tax Number</Label>
                {editing ? (
                  <Input
                    value={formData.tax_number || ''}
                    onChange={(e) => handleInputChange('tax_number', e.target.value)}
                    placeholder="Tax number"
                  />
                ) : (
                  <p className="text-slate-900 dark:text-slate-100 font-medium">{profile.tax_number || 'Not provided'}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Iter88k — Resignation request history card */}
      {myResignations.length > 0 && (
        <div
          data-testid="my-resignations-card"
          className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 overflow-hidden"
        >
          <div className="p-6 border-b border-slate-100 dark:border-slate-700">
            <h4 className="text-lg font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <LogOut className="h-5 w-5 text-red-600" />
              My Resignation Requests
            </h4>
          </div>
          <div className="divide-y divide-slate-100 dark:divide-slate-700">
            {myResignations.map((r) => (
              <div key={r.resignation_id} className="p-6 flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                      {r.resignation_id}
                    </span>
                    <span
                      className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        r.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                        r.status === 'rejected' ? 'bg-red-100 text-red-700' :
                        r.status === 'withdrawn' ? 'bg-slate-200 text-slate-700' :
                        'bg-amber-100 text-amber-700'
                      }`}
                    >
                      {r.status?.toUpperCase()}
                    </span>
                  </div>
                  <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                    Last working day: <strong>{r.last_working_day}</strong>
                    {' · '}Notice: {r.notice_period_days || 0} day(s)
                  </p>
                  <p className="text-sm text-slate-700 dark:text-slate-300 mt-1">
                    <em>Reason:</em> {r.reason}
                  </p>
                  {r.hr_response && (
                    <p className="text-sm text-slate-700 dark:text-slate-300 mt-1">
                      <em>HR response:</em> {r.hr_response}
                    </p>
                  )}
                </div>
                {r.status === 'pending' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => withdrawResignation(r.resignation_id)}
                    data-testid={`withdraw-resignation-${r.resignation_id}`}
                  >
                    Withdraw
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Iter88k — Resignation submission dialog */}
      <Dialog open={resignDialogOpen} onOpenChange={setResignDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <LogOut className="h-5 w-5" />
              Submit Resignation Request
            </DialogTitle>
            <DialogDescription>
              Your request will be sent to HR for review. You can withdraw it any time before HR responds.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/40 rounded-lg text-sm text-amber-800 dark:text-amber-200">
              <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <p>
                Once HR approves your resignation, your account will be marked as terminated on your last working day.
                This action cannot be reversed without HR intervention.
              </p>
            </div>
            <div>
              <Label>Proposed Last Working Day *</Label>
              <Input
                data-testid="resign-last-working-day"
                type="date"
                value={resignForm.last_working_day}
                /* Iter88v — Earliest selectable date = today + notice period.
                   Prevents employees from typing a past or notice-violating
                   date. Backend re-validates as a safety net. */
                min={(() => {
                  const d = new Date(Date.now() + 8 * 3600 * 1000);
                  d.setUTCDate(d.getUTCDate() + (Number(policyNoticeDays) || 0));
                  return d.toISOString().slice(0, 10);
                })()}
                onChange={(e) => setResignForm({ ...resignForm, last_working_day: e.target.value })}
                className="mt-1"
              />
              <p className="text-xs text-slate-500 mt-1">
                Earliest valid date based on your {policyNoticeDays}-day notice period:{' '}
                <strong className="text-slate-700 dark:text-slate-300">
                  {(() => {
                    const d = new Date(Date.now() + 8 * 3600 * 1000);
                    d.setUTCDate(d.getUTCDate() + (Number(policyNoticeDays) || 0));
                    return d.toISOString().slice(0, 10);
                  })()}
                </strong>
              </p>
            </div>
            <div>
              <Label className="text-slate-600 dark:text-slate-400">Notice Period (per company HR policy)</Label>
              <div
                data-testid="resign-notice-period-readonly"
                className="mt-1 px-3 py-2 rounded-lg bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-200 flex items-center justify-between"
              >
                <span className="font-medium">{policyNoticeDays} day(s)</span>
                <span className="text-xs text-slate-500">Set by HR policy · HR may override at review</span>
              </div>
            </div>
            <div>
              <Label>Reason for Resignation *</Label>
              <Textarea
                data-testid="resign-reason"
                value={resignForm.reason}
                onChange={(e) => setResignForm({ ...resignForm, reason: e.target.value })}
                placeholder="Please share why you've chosen to resign. This helps HR with exit feedback."
                rows={4}
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setResignDialogOpen(false)}
              disabled={resignSubmitting}
              data-testid="resign-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={submitResignation}
              disabled={resignSubmitting}
              data-testid="resign-submit-btn"
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {resignSubmitting ? 'Submitting…' : 'Submit Resignation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
